import { useEffect, useRef } from "react";
import { getBassLevel } from "../lib/audio";
import { isAlly } from "../lib/theme";

const COLORS = ["57,255,20", "255,0,85", "0,240,255", "57,255,20", "255,0,85", "0,240,255"];
const ALLY_COLORS = ["255,113,206", "1,205,254", "255,251,150", "185,103,255", "5,255,161", "255,150,80"];

export const LaserCanvas = () => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.4 });
  const colors = useRef(isAlly() ? ALLY_COLORS : COLORS);

  useEffect(() => {
    const onTheme = (e) => {
      colors.current = e.detail.ally ? ALLY_COLORS : COLORS;
    };
    window.addEventListener("rotu:theme", onTheme);
    return () => window.removeEventListener("rotu:theme", onTheme);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      mouse.current.x = e.clientX / window.innerWidth;
      mouse.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener("pointermove", onMove);

    const draw = () => {
      const bass = getBassLevel();
      document.documentElement.style.setProperty("--bass", bass.toFixed(3));
      t += 0.006 + bass * 0.03;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const w = canvas.width;
      const h = canvas.height;
      const origins = [0.12, 0.3, 0.5, 0.7, 0.88, 0.5];
      const palette = colors.current;

      origins.forEach((ox, i) => {
        const swing =
          Math.sin(t * (0.7 + i * 0.13) + i * 1.7) * (0.5 + bass * 0.9) +
          (mouse.current.x - 0.5) * 0.9;
        const angle = Math.PI / 2 + swing * 0.7;
        const len = h * 1.6;
        const x0 = ox * w;
        const y0 = -20;
        const x1 = x0 + Math.cos(angle) * len;
        const y1 = y0 + Math.sin(angle) * len;

        const grad = ctx.createLinearGradient(x0, y0, x1, y1);
        grad.addColorStop(0, `rgba(${palette[i]},${0.32 + bass * 0.4})`);
        grad.addColorStop(0.6, `rgba(${palette[i]},${0.08 + bass * 0.15})`);
        grad.addColorStop(1, `rgba(${palette[i]},0)`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6 + bass * 3.5;
        ctx.shadowColor = `rgba(${palette[i]},0.8)`;
        ctx.shadowBlur = 12 + bass * 45;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
      });

      if (bass > 0.4) {
        const strobe = palette[1];
        const g = ctx.createRadialGradient(w / 2, h, 0, w / 2, h, h * 0.8);
        g.addColorStop(0, `rgba(${strobe},${(bass - 0.4) * 0.35})`);
        g.addColorStop(1, `rgba(${strobe},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="laser-canvas"
      className="fixed inset-0 z-[1] pointer-events-none"
    />
  );
};
